/**
 * The landing view: where the market is, before you have picked a symbol.
 *
 * Every row here is an ETF, not an index. Alpaca serves US equities and
 * nothing else, so there is no SPX, no VIX, and no Nikkei to be had -- SPY
 * tracks the S&P 500 but is not it, and the two differ by dividends,
 * expenses and a tracking error that widens over long horizons. The label
 * says which is which rather than quietly printing "S&P 500" over ETF data,
 * because someone reading a level off this page and comparing it to a quote
 * elsewhere needs to know why the numbers disagree.
 */
import { formatPrice } from "@tt/shared";
import { AreaSeries, ColorType, createChart, type IChartApi } from "lightweight-charts";
import { useEffect, useMemo, useRef } from "react";

import { useAsync } from "../hooks/useApi";
import { api } from "../lib/api";

interface Row {
  symbol: string;
  name: string;
  /** What it actually tracks, when that differs from the name. */
  tracks?: string;
}

const INDICES: Row[] = [
  { symbol: "SPY", name: "S&P 500", tracks: "SPDR S&P 500 ETF" },
  { symbol: "QQQ", name: "Nasdaq 100", tracks: "Invesco QQQ Trust" },
  { symbol: "DIA", name: "Dow Jones", tracks: "SPDR Dow Jones ETF" },
  { symbol: "IWM", name: "Russell 2000", tracks: "iShares Russell 2000 ETF" },
];

const SECTORS: Row[] = [
  { symbol: "XLK", name: "Technology" },
  { symbol: "XLF", name: "Financials" },
  { symbol: "XLV", name: "Health care" },
  { symbol: "XLY", name: "Consumer disc." },
  { symbol: "XLP", name: "Consumer staples" },
  { symbol: "XLE", name: "Energy" },
  { symbol: "XLI", name: "Industrials" },
  { symbol: "XLU", name: "Utilities" },
  { symbol: "XLB", name: "Materials" },
  { symbol: "XLRE", name: "Real estate" },
  { symbol: "XLC", name: "Communication" },
];

const ALL = [...INDICES, ...SECTORS].map((r) => r.symbol);
const HERO = "SPY";

export function MarketOverview({ onPick }: { onPick: (symbol: string) => void }) {
  const quotes = useAsync((s) => api.quotes(ALL, s), []);
  // A day of five-minute bars: enough shape to read the session, few enough
  // points that the sparkline stays legible at this size.
  const hero = useAsync((s) => api.bars(HERO, "5m", 78, false, s), []);

  const q = quotes.data?.quotes ?? {};
  const heroQuote = q[HERO];

  const leaders = useMemo(() => {
    const scored = SECTORS
      .map((r) => ({ ...r, pct: q[r.symbol]?.change_pct ?? null }))
      .filter((r): r is Row & { pct: number } => r.pct !== null);
    scored.sort((a, b) => b.pct - a.pct);
    return scored;
  }, [q]);

  return (
    <div className="panel overview">
      <div className="ov-grid">
        <section className="ov-hero card">
          <button className="ov-hero-head" onClick={() => onPick(HERO)}>
            <span className="ov-badge spx">500</span>
            <span className="ov-hero-id">
              <span className="ov-hero-name">S&P 500</span>
              <span className="ov-ticker">{HERO}</span>
            </span>
            {heroQuote && (
              <span className="ov-hero-px">
                <span className="px">{formatPrice(heroQuote.price)}</span>
                <Pct value={heroQuote.change_pct} big />
              </span>
            )}
          </button>
          <Sparkline bars={hero.data?.bars ?? []} up={(heroQuote?.change_pct ?? 0) >= 0} />
          {hero.loading && !hero.data && (
            <div className="hint"><span className="spinner" /> loading…</div>
          )}
        </section>

        <section className="ov-list card">
          <h3>Major indices</h3>
          <div className="ov-scroll">
            {INDICES.map((r) => (
              <QuoteRow key={r.symbol} row={r} q={q[r.symbol]} onPick={onPick} />
            ))}
          </div>
          <p className="muted small ov-note">
            These are the ETFs that track each index, not the index itself —
            the only form this platform's data provider carries. They differ by
            dividends, expenses and tracking error.
          </p>
        </section>

        <section className="ov-list card ov-sectors">
          <h3>Sectors — best to worst today</h3>
          <div className="ov-scroll">
            {leaders.length === 0 && quotes.loading && (
              <div className="hint"><span className="spinner" /> loading…</div>
            )}
            {leaders.map((r) => (
              <QuoteRow key={r.symbol} row={r} q={q[r.symbol]} onPick={onPick} bar />
            ))}
          </div>
          <p className="muted small ov-note">
            One day of sector rotation is noise far more often than it is a
            signal. Ordering says what moved, not what is working.
          </p>
        </section>

        <section className="ov-list card ov-news">
          <h3>Market news</h3>
          <MarketNews onPick={onPick} />
        </section>
      </div>
    </div>
  );
}

function MarketNews({ onPick }: { onPick: (symbol: string) => void }) {
  // Market-wide feed (no symbol filter) is the right default on a landing
  // page: you're there to see what moved, not to drill into one name.
  const feed = useAsync((s) => api.news(null, 3, 25, s), []);
  if (feed.loading && !feed.data) {
    return <div className="hint"><span className="spinner" /> loading…</div>;
  }
  const items = feed.data?.news ?? [];
  if (items.length === 0) {
    return <p className="muted small">No stories in the last three days.</p>;
  }
  return (
    <div className="ov-news-list">
      {items.slice(0, 15).map((item) => (
        <div className="ov-news-row" key={item.id}>
          <a href={item.url ?? "#"} target="_blank" rel="noopener noreferrer"
             className="ov-news-headline">
            {item.headline}
          </a>
          <div className="ov-news-meta mono small faint">
            {item.source}
            {item.symbols?.slice(0, 3).map((s) => (
              <button key={s} className="ov-news-chip" onClick={() => onPick(s)}>
                {s}
              </button>
            ))}
            {item.published_at && (
              <span>· {new Date(item.published_at).toLocaleString("en-US", {
                month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
              })}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function QuoteRow({
  row, q, onPick, bar = false,
}: {
  row: Row;
  q?: { price: number; change_pct?: number | null };
  onPick: (s: string) => void;
  bar?: boolean;
}) {
  const pct = q?.change_pct ?? null;
  return (
    <button className="ov-row" onClick={() => onPick(row.symbol)} title={row.tracks ?? row.name}>
      <span className="ov-row-id">
        <span className="ov-row-name">{row.name}</span>
        <span className="ov-ticker">{row.symbol}</span>
      </span>
      {/* A bar as well as a number: ordering alone does not show whether the
          spread between first and last is a rotation or a rounding error. */}
      {bar && pct !== null && (
        <span className="ov-bar">
          <span
            className={pct >= 0 ? "up" : "down"}
            style={{ width: `${Math.min(100, Math.abs(pct) * 25)}%` }}
          />
        </span>
      )}
      <span className="ov-row-px">
        {q ? formatPrice(q.price) : "—"}
      </span>
      <Pct value={pct} />
    </button>
  );
}

function Pct({ value, big = false }: { value?: number | null; big?: boolean }) {
  if (value === null || value === undefined) return <span className="chg flat">—</span>;
  const tone = value > 0 ? "up" : value < 0 ? "down" : "flat";
  return (
    <span className={`chg ${tone} ${big ? "big" : ""}`}>
      {value > 0 ? "+" : ""}{value.toFixed(2)}%
    </span>
  );
}

/** A filled area of the session, in the direction the day is going. */
function Sparkline({ bars, up }: { bars: { time: number; close: number }[]; up: boolean }) {
  const host = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  // React runs effect cleanups in the order the effects were declared, so on
  // unmount the chart is destroyed before the series cleanup below gets to
  // run -- and removeSeries on a destroyed chart throws from inside
  // lightweight-charts. With no error boundary above it that took the whole
  // app blank. The flag makes the series cleanup a no-op once the chart is
  // gone; setting chart.current = null is not enough, because the series
  // effect captured its own reference.
  const disposed = useRef(false);

  useEffect(() => {
    if (!host.current) return;
    const c = createChart(host.current, {
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#626d80",
        fontSize: 11.5,
        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
        attributionLogo: false,
      },
      grid: { vertLines: { visible: false }, horzLines: { color: "#151a24" } },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.12, bottom: 0.04 } },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false },
      crosshair: { mode: 0 },
      handleScroll: false,
      handleScale: false,
      autoSize: true,
    });
    chart.current = c;
    disposed.current = false;
    return () => {
      disposed.current = true;
      c.remove();
      chart.current = null;
    };
  }, []);

  useEffect(() => {
    const c = chart.current;
    if (!c || bars.length === 0) return;
    const colour = up ? "#22c55e" : "#f04452";
    // v4 API: addAreaSeries, not the v5 addSeries(AreaSeries, ...).
    const s = c.addSeries(AreaSeries, {
      lineColor: colour,
      lineWidth: 2,
      topColor: `${colour}44`,
      bottomColor: `${colour}05`,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    s.setData(bars.map((b) => ({ time: b.time as never, value: b.close })));
    c.timeScale().fitContent();
    return () => {
      if (!disposed.current) c.removeSeries(s);
    };
  }, [bars, up]);

  return <div ref={host} className="ov-spark" />;
}
